import { useState } from 'react';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '@/components/ui/sheet';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Search, Loader2 } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

interface SearchReplaceSheetProps {
  isOpen: boolean;
  onClose: () => void;
  initialQuery: string;
  onReplace: (newBookId: string, newBook: { title: string; authors: string[]; cover_url?: string }) => void;
}

interface SearchResult {
  id: string;
  title: string;
  authors: string[];
  cover_url?: string;
  published_year?: number;
}

export function SearchReplaceSheet({ isOpen, onClose, initialQuery, onReplace }: SearchReplaceSheetProps) {
  const [searchQuery, setSearchQuery] = useState(initialQuery);
  const [searchResults, setSearchResults] = useState<SearchResult[]>([]);
  const [isSearching, setIsSearching] = useState(false);

  const handleSearch = async () => {
    if (!searchQuery.trim()) return;

    setIsSearching(true);
    try {
      const { data, error } = await supabase.functions.invoke('search-books', {
        body: { query: searchQuery, limit: 20 }
      });

      if (error) throw error;

      setSearchResults(data?.results || []);
      
      if (data?.results?.length === 0) {
        toast.info('No results found', {
          description: 'Try adjusting your search terms'
        });
      }
    } catch (error) {
      console.error('Search error:', error);
      toast.error('Search failed', {
        description: 'Please try again'
      });
    } finally {
      setIsSearching(false);
    }
  };

  const handleSelectBook = (book: SearchResult) => {
    onReplace(book.id, {
      title: book.title,
      authors: book.authors,
      cover_url: book.cover_url
    });
    onClose();
    toast.success('Book replaced successfully');
  };

  return (
    <Sheet open={isOpen} onOpenChange={onClose}>
      <SheetContent side="right" className="w-full sm:max-w-md">
        <SheetHeader>
          <SheetTitle>Search for the correct book</SheetTitle>
          <SheetDescription>
            If the matched book isn't correct, search for and select the right one
          </SheetDescription>
        </SheetHeader>

        <div className="space-y-4 mt-6">
          <div className="flex gap-2">
            <Input
              placeholder="Search by title or author..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
            />
            <Button onClick={handleSearch} disabled={isSearching} size="icon">
              {isSearching ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Search className="h-4 w-4" />
              )}
            </Button>
          </div>

          <ScrollArea className="h-[calc(100vh-200px)]">
            {searchResults.length > 0 ? (
              <div className="space-y-3">
                {searchResults.map((book) => (
                  <div
                    key={book.id}
                    className="flex gap-3 p-3 border rounded-lg hover:bg-accent/50 transition-colors"
                  >
                    {book.cover_url ? (
                      <img
                        src={book.cover_url}
                        alt={book.title}
                        className="w-16 h-24 object-cover rounded"
                      />
                    ) : (
                      <div className="w-16 h-24 bg-muted rounded flex items-center justify-center text-xs text-muted-foreground">
                        No cover
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <h4 className="font-medium line-clamp-2">{book.title}</h4>
                      <p className="text-sm text-muted-foreground line-clamp-1">
                        {book.authors.join(', ')}
                      </p>
                      {book.published_year && (
                        <p className="text-xs text-muted-foreground">{book.published_year}</p>
                      )}
                      <Button
                        size="sm"
                        variant="outline"
                        className="mt-2"
                        onClick={() => handleSelectBook(book)}
                      >
                        Use This Book
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center text-muted-foreground py-8">
                {isSearching ? 'Searching...' : 'Search for a book to see results'}
              </div>
            )}
          </ScrollArea>
        </div>
      </SheetContent>
    </Sheet>
  );
}
